// DEITY — talk to the faculty about the Step in front of you. The conversation
// is the control layer (the deity can write to its tool through
// /api/ai/deity); "Open instrument" drops into the structured tool, already
// in context.
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  useWindowDimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { TIER_LABEL } from '../olmps/ascension';
import { useSyqiChat } from '../olmps/useSyqiChat';
import type { NowStep } from './useCurrentStep';
import type { SeatView } from './useSelf';
import { GEAR_LABEL, SEAT_BY_ID, portraitFor } from './roster';
import { Display, GoldButton, Kicker } from './ui';
import { SECTOR_NAME, makeStyles, useV10 } from './theme';

export function DeitySheet({
  seatId,
  seatView,
  step,
  onClose,
  onOpenTool,
}: {
  seatId: string | null;
  seatView: SeatView | undefined;
  step: NowStep | null;
  onClose: () => void;
  onOpenTool: (seatId: string) => void;
}) {
  const seat = seatId ? SEAT_BY_ID[seatId] : null;
  return (
    <Modal visible={!!seat} animationType="slide" transparent onRequestClose={onClose}>
      {seat ? <DeityBody seatId={seat.id} seatView={seatView} step={step} onClose={onClose} onOpenTool={onOpenTool} /> : null}
    </Modal>
  );
}

function DeityBody({
  seatId,
  seatView,
  step,
  onClose,
  onOpenTool,
}: {
  seatId: string;
  seatView: SeatView | undefined;
  step: NowStep | null;
  onClose: () => void;
  onOpenTool: (seatId: string) => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const seat = SEAT_BY_ID[seatId];
  const { messages, sending, send } = useSyqiChat({ toolId: seat.shellTool, endpoint: '/api/ai/deity' });
  const [draft, setDraft] = useState('');
  const scroll = useRef<ScrollView>(null);
  useEffect(() => {
    setTimeout(() => scroll.current?.scrollToEnd({ animated: true }), 60);
  }, [messages.length]);

  const stepContext = deityStepContext(step);
  const opener = deityOpener(seat.id, step);
  const suggestions = deitySuggestions(step);

  const submit = (text: string) => {
    const tx = text.trim();
    if (!tx) return;
    setDraft('');
    void send(tx, undefined, stepContext);
  };

  const portrait = portraitFor(seat.id);
  const { width: winW } = useWindowDimensions();
  const heroImgW = Math.min(winW, 480);
  const heroFade = t.mode === 'dark' ? (['rgba(7,11,23,0)', 'rgba(7,11,23,0.6)', t.night] as const) : (['rgba(225,240,255,0)', 'rgba(225,240,255,0.55)', t.night] as const);
  return (
    <View style={styles.backdrop}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.sheet}>
        <View style={styles.hero}>
          {portrait ? (
            // Natural 3:4 portrait anchored at the top, never wider than a
            // phone, so the head always shows; sides fade into the night.
            <Image
              source={portrait}
              style={{ position: 'absolute', top: 0, left: (winW - heroImgW) / 2, width: heroImgW, height: heroImgW * (4 / 3) }}
              resizeMode="cover"
            />
          ) : null}
          {winW > heroImgW ? (
            <>
              <LinearGradient colors={[t.night, `${t.night}00`]} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={{ position: 'absolute', top: 0, bottom: 0, left: (winW - heroImgW) / 2 - 1, width: 90 }} />
              <LinearGradient colors={[`${t.night}00`, t.night]} start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }} style={{ position: 'absolute', top: 0, bottom: 0, right: (winW - heroImgW) / 2 - 1, width: 90 }} />
            </>
          ) : null}
          <LinearGradient colors={heroFade} style={StyleSheet.absoluteFill} />
          <Pressable onPress={onClose} style={styles.close} hitSlop={10}>
            <Text style={styles.closeText}>✕</Text>
          </Pressable>
          <View style={styles.heroText}>
            <Kicker>
              {GEAR_LABEL[seat.gear].name} · {TIER_LABEL[seat.tier]}
            </Kicker>
            <Display size={34}>{seat.name}</Display>
            <Text style={styles.ability}>the ability to {seat.ability}</Text>
            {seatView ? (
              <Text style={styles.state}>
                {[seatView.acquisition === 'acquired_unseated' ? 'Acquired · unseated' : seatView.ladderLine, seatView.stats.secured ? null : seatView.stats.next, seatView.seatLine]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            ) : null}
          </View>
        </View>

        <ScrollView ref={scroll} style={{ flex: 1 }} contentContainerStyle={styles.chat}>
          <Bubble who="deity" text={opener} />
          {messages.map((m, i) => (
            <Bubble key={i} who={m.role === 'user' ? 'user' : 'deity'} text={m.content} error={(m as { isError?: boolean }).isError} />
          ))}
          {sending ? <ActivityIndicator color={t.gold} style={{ alignSelf: 'flex-start', margin: 8 }} /> : null}
          {messages.length === 0 ? (
            <View style={styles.suggest}>
              {suggestions.map((s) => (
                <Pressable key={s} onPress={() => submit(s)} style={styles.chip}>
                  <Text style={styles.chipText}>{s}</Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </ScrollView>

        <View style={styles.inputRow}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            placeholder={`Talk to ${seat.name}…`}
            placeholderTextColor="#8a8378"
            style={styles.input}
            onSubmitEditing={() => submit(draft)}
            returnKeyType="send"
          />
          <Pressable onPress={() => submit(draft)} style={styles.send}>
            <Text style={styles.sendText}>➤</Text>
          </Pressable>
        </View>
        <GoldButton label={`Open the ${seat.name} instrument`} ghost onPress={() => onOpenTool(seat.id)} style={{ marginHorizontal: 16, marginBottom: 16 }} />
      </KeyboardAvoidingView>
    </View>
  );
}

/** What the deity knows about the Step in front of you (sent with each message). */
export function deityStepContext(step: NowStep | null) {
  return step
    ? {
        title: step.title,
        sector: step.sector ? SECTOR_NAME[step.sector] : null,
        phase: GEAR_LABEL[step.gear].name,
        when: step.when,
        why: step.why,
        endeavour: step.trail.endeavour ?? null,
        aim: step.trail.aim ?? null,
        project: step.trail.project ?? null,
        source: step.source,
      }
    : undefined;
}

export function deityOpener(seatId: string, step: NowStep | null) {
  const seat = SEAT_BY_ID[seatId];
  return step
    ? `You're on “${step.title}”. ${GEAR_LABEL[step.gear].question} Tell me where you're stuck, or ask me to sort it.`
    : `I am ${seat.name} — the ability to ${seat.ability}. What do you want to work on?`;
}

export function deitySuggestions(step: NowStep | null) {
  return step
    ? ['What’s the first move?', 'Break this down for me', 'Move it to a better time', 'Why does this matter?']
    : ['What should I focus on?', 'Show me my progress', 'Give me today’s principle'];
}

function Bubble({ who, text, error }: { who: 'user' | 'deity'; text: string; error?: boolean }) {
  const t = useV10();
  const styles = useStyles();
  const mine = who === 'user';
  return (
    <View style={[styles.bubble, mine ? styles.mine : styles.theirs, error && { borderColor: t.lapsed }]}>
      <Text style={[styles.bubbleText, mine && { color: t.onGold }]}>{text}</Text>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
    sheet: { height: '94%', backgroundColor: t.night, borderTopLeftRadius: 28, borderTopRightRadius: 28, overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine },
    hero: { height: 300, overflow: 'hidden', backgroundColor: t.night },
    heroImg: { position: 'absolute', width: '100%', height: 420, top: -20 },
    close: { position: 'absolute', top: 14, right: 16, width: 34, height: 34, borderRadius: 17, backgroundColor: t.glassStrong, alignItems: 'center', justifyContent: 'center' },
    closeText: { color: t.ivory, fontSize: 16 },
    heroText: { position: 'absolute', left: 20, right: 20, bottom: 14, gap: 4 },
    ability: { color: t.ivorySoft, fontStyle: 'italic', fontSize: 14 },
    state: { color: t.goldBright, fontSize: 12, fontWeight: '700', marginTop: 2 },
    chat: { padding: 16, gap: 10 },
    bubble: { maxWidth: '86%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10, borderWidth: 1 },
    theirs: { alignSelf: 'flex-start', backgroundColor: t.navyRaised, borderColor: t.navyLine },
    mine: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    bubbleText: { color: t.ivory, fontSize: 15, lineHeight: 21 },
    suggest: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
    chip: { borderWidth: 1, borderColor: t.navyLine, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7 },
    chipText: { color: t.goldBright, fontSize: 13 },
    inputRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingVertical: 10 },
    input: { flex: 1, height: 46, borderRadius: 23, backgroundColor: '#ffffff', paddingHorizontal: 16, color: '#111', fontSize: 15, borderWidth: 1, borderColor: t.gold },
    send: { width: 46, height: 46, borderRadius: 23, backgroundColor: t.gold, alignItems: 'center', justifyContent: 'center' },
    sendText: { color: t.onGold, fontSize: 18, fontWeight: '900' },
  }),
);
